import {PlatformedQueryFilters, q, resetQueries} from "..";
import {nominate, ProductType} from "platformed-client/types";
import api, {RedirectError} from "../../api";
import {RedirectTarget, redirectRestoringUrl} from "../../utils/auth";
import {param, typedQuery} from "../typing";
import {getInferredLanguage, setLanguage} from "../../App/i18n";

function authenticatedQueries(): PlatformedQueryFilters[] {
  return [
    nominate("platformedQueryFilters", {
      predicate(query) {
        return query.queryKey.length !== 1 || query.queryKey[0] !== "unauthenticated";
      },
    } as const),
  ];
}

export const whoAmI = typedQuery(["whoAmI"], async () => {
  // Important to use "reset" as "invalidate" doesn't reset error states
  await resetQueries([q.unauthenticated.filter()]);
  const whoami = await api.auth.whoAmI();
  if (whoami.user.language_code) {
    // Set the language in the i18n system
    setLanguage(whoami.user.language_code);
  } else {
    // If no language is set, infer it and update the server
    //
    // Purposefully omitting `await` - it's important this happens asynchronously
    // because `changeLanguage` invalidates `whoAmI` which could cause a
    // deadlock if we `await` here.
    api.auth.changeLanguage(getInferredLanguage());
  }
  return whoami;
});

export const whoAmIOnAccount = typedQuery(["whoAmIOnAccount"], async () => {
  // Important to use "reset" as "invalidate" doesn't reset error states
  await resetQueries([q.unauthenticated.filter()]);
  return await api.auth.whoAmIOnAccount();
});

export const unauthenticated = typedQuery(["unauthenticated"], async () => {
  try {
    // Important to use "reset" as "invalidate" doesn't reset error states
    await resetQueries(authenticatedQueries());
    await api.auth.whoAmI();

    throw new RedirectError(redirectRestoringUrl());
  } catch (ex) {
    if (ex instanceof RedirectError && ex.response.target === RedirectTarget.Login) {
      return {};
    } else {
      throw ex;
    }
  }
});

export const accounts = typedQuery(["accounts"], async () => {
  return await api.auth.listAccounts();
});

export const consent = typedQuery(["consent", param("externalRedirect")], async externalRedirect => {
  return await api.accessTokens.consent({external_redirect: externalRedirect});
});

export const logSession = typedQuery(
  ["session", "log", param<ProductType>("productType")],
  async (productType: ProductType) => {
    return await api.auth.logSession({product: productType});
  },
);
